/* ============================================================
   KENLEY STUDIOS — Theme JS v4
   100% custom. No Dawn dependency.
   v4: initAll() pattern fires on DOMContentLoaded AND shopify:section:load
   ============================================================ */

'use strict';

/* ── Helpers ─────────────────────────────────────────────── */
const qs  = (sel, ctx = document) => ctx.querySelector(sel);
const qsa = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

function formatMoney(cents) {
  const dollars = (cents / 100).toFixed(2);
  return '$' + dollars.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/* ── Scroll Reveal ───────────────────────────────────────── */
function initReveal(scope = document) {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.1, rootMargin: '0px 0px -48px 0px' });

  qsa('.ks-reveal', scope).forEach(el => observer.observe(el));
}

/* ── Fixed Pill Header — scroll state ───────────────────── */
function initStickyHeader(scope = document) {
  const header = qs('.ks-header', scope);
  if (!header) return;

  let ticking = false;
  function update() {
    header.classList.toggle('is-scrolled', window.scrollY > 40);
    ticking = false;
  }

  window.addEventListener('scroll', () => {
    if (!ticking) {
      requestAnimationFrame(update);
      ticking = true;
    }
  }, { passive: true });

  update();
}

/* ── Mobile Nav ──────────────────────────────────────────── */
function initMobileNav(scope = document) {
  const hamburger = qs('.ks-hamburger', scope);
  const nav       = qs('.ks-mobile-nav', scope);
  if (!hamburger || !nav) return;

  const links = qsa('.ks-mobile-nav__link', nav);

  function open() {
    hamburger.classList.add('is-open');
    hamburger.setAttribute('aria-expanded', 'true');
    nav.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    links.forEach(l => l.setAttribute('tabindex', '0'));
  }

  function close() {
    hamburger.classList.remove('is-open');
    hamburger.setAttribute('aria-expanded', 'false');
    nav.classList.remove('is-open');
    document.body.style.overflow = '';
    links.forEach(l => l.setAttribute('tabindex', '-1'));
  }

  hamburger.addEventListener('click', () => {
    hamburger.classList.contains('is-open') ? close() : open();
  });

  links.forEach(l => l.addEventListener('click', close));

  document.addEventListener('keydown', e => {
    if (e.key === 'Escape') close();
  });
}

/* ── Accordion ───────────────────────────────────────────── */
function initAccordion(scope = document) {
  qsa('.ks-accordion__trigger', scope).forEach(trigger => {
    trigger.addEventListener('click', () => {
      const panel    = trigger.nextElementSibling;
      const expanded = trigger.getAttribute('aria-expanded') === 'true';

      const accordion = trigger.closest('.ks-accordion');
      qsa('.ks-accordion__trigger', accordion).forEach(t => {
        if (t !== trigger) {
          t.setAttribute('aria-expanded', 'false');
          t.nextElementSibling?.classList.remove('is-open');
        }
      });

      trigger.setAttribute('aria-expanded', !expanded ? 'true' : 'false');
      panel?.classList.toggle('is-open', !expanded);
    });
  });
}

/* ── PDP: Size Pills ─────────────────────────────────────── */
function initSizePills(scope = document) {
  const pills    = qsa('[data-size-pill]', scope);
  const idInput  = qs('input[name="id"]', scope);
  const atcBtn   = qs('[data-atc-btn]', scope);
  const invBadge = qs('[data-inventory-badge]', scope);
  const invCount = qs('[data-inventory-count]', scope);
  if (!pills.length) return;

  pills.forEach(pill => {
    pill.addEventListener('click', () => {
      pills.forEach(p => p.classList.remove('is-selected'));
      pill.classList.add('is-selected');

      const variantId = pill.dataset.variantId;
      const inventory = parseInt(pill.dataset.inventory) || 0;
      const soldOut   = pill.hasAttribute('data-sold-out') && pill.dataset.soldOut !== 'false';

      if (idInput) idInput.value = variantId;

      if (atcBtn) {
        if (soldOut) {
          atcBtn.disabled = true;
          atcBtn.textContent = 'Sold Out';
        } else {
          atcBtn.disabled = false;
          const priceEl = qs('[data-variant-price]', scope);
          if (priceEl) {
            atcBtn.textContent = 'Add to Cart — ' + (priceEl.dataset.variantPrices?.[variantId] || 'Add to Cart');
          }
        }
      }

      if (invBadge) {
        if (!soldOut && inventory > 0 && inventory <= 10) {
          invBadge.hidden = false;
          if (invCount) invCount.textContent = inventory;
        } else {
          invBadge.hidden = true;
        }
      }
    });
  });
}

/* ── PDP: Desktop Gallery ────────────────────────────────── */
function initGallery(scope = document) {
  const thumbs = qsa('[data-gallery-thumb]', scope);
  const slides = qsa('[data-gallery-slide]', scope);
  if (!thumbs.length || !slides.length) return;

  function activate(index) {
    thumbs.forEach((t, i) => {
      t.classList.toggle('is-active', i === index);
      t.setAttribute('aria-selected', i === index ? 'true' : 'false');
      t.setAttribute('tabindex', i === index ? '0' : '-1');
    });
    slides.forEach((s, i) => s.classList.toggle('is-active', i === index));
  }

  thumbs.forEach((thumb, i) => {
    thumb.addEventListener('click', () => activate(i));
    thumb.addEventListener('keydown', e => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); activate(i); }
    });
  });
}

/* ── PDP: Mobile Gallery Dots ────────────────────────────── */
function initMobileGalleryDots(scope = document) {
  const carousel = qs('.ks-pdp-gallery__mobile', scope);
  const dots     = qsa('.ks-pdp-gallery__dot', scope);
  if (!carousel || !dots.length) return;

  const slides = qsa('.ks-pdp-gallery__mobile-slide', carousel);

  const obs = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const idx = slides.indexOf(entry.target);
        dots.forEach((d, i) => d.classList.toggle('is-active', i === idx));
      }
    });
  }, { root: carousel, threshold: 0.5 });

  slides.forEach(s => obs.observe(s));

  dots.forEach((dot, i) => {
    dot.addEventListener('click', () => {
      slides[i]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    });
  });
}

/* ── Size Guide Modal ────────────────────────────────────── */
function initSizeGuide(scope = document) {
  const modal    = qs('[data-size-guide]', scope);
  const backdrop = qs('#ks-size-guide-backdrop', scope);
  if (!modal) return;

  function open() {
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    if (backdrop) backdrop.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    qs('.ks-modal__close', modal)?.focus();
  }

  function close() {
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    if (backdrop) backdrop.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  document.addEventListener('click', e => {
    if (e.target.closest('[data-size-guide-open]'))  { open(); return; }
    if (e.target.closest('[data-size-guide-close]')) { close(); return; }
  });

  backdrop?.addEventListener('click', close);
  document.addEventListener('keydown', e => { if (e.key === 'Escape') close(); });
}

/* ── Email Capture ───────────────────────────────────────── */
function initEmailCapture(scope = document) {
  qsa('.ks-email-form', scope).forEach(form => {
    form.addEventListener('submit', async e => {
      e.preventDefault();
      const input   = form.querySelector('.ks-email-form__input');
      const email   = input?.value.trim();
      const wrap    = form.closest('.ks-email-capture, .ks-pdp-email-capture');
      const success = wrap?.querySelector('.ks-email-capture__success');
      if (!email) return;

      if (document.cookie.includes('ks_klv_captured=1')) {
        if (success) success.classList.add('is-visible');
        form.classList.add('is-submitted');
        return;
      }

      try {
        if (window._klOnsite) {
          window._klOnsite.push(['identify', { '$email': email }]);
        }
      } catch (_) {}

      const exp = new Date();
      exp.setFullYear(exp.getFullYear() + 1);
      document.cookie = `ks_klv_captured=1; path=/; expires=${exp.toUTCString()}; SameSite=Lax`;

      form.classList.add('is-submitted');
      if (success) success.classList.add('is-visible');
    });
  });
}

/* ── Scroll Tilt ─────────────────────────────────────────── */
function initScrollTilt(scope = document) {
  const sections = qsa('[data-scroll-tilt]', scope);
  if (!sections.length) return;

  sections.forEach(section => {
    const inner = section.querySelector('[data-scroll-tilt-inner]');
    if (!inner) return;

    function update() {
      const rect     = section.getBoundingClientRect();
      const winH     = window.innerHeight;
      const progress = Math.max(0, Math.min(1, (winH - rect.top) / winH));
      const rotateX  = 14 * (1 - progress);
      const scale    = 1.04 - (0.04 * progress);
      const opacity  = 0.4 + (0.6 * progress);
      inner.style.transform = `perspective(1200px) rotateX(${rotateX}deg) scale(${scale})`;
      inner.style.opacity   = opacity;
    }

    window.addEventListener('scroll', update, { passive: true });
    update();
  });
}

/* ── Cart page AJAX qty ──────────────────────────────────── */
function initCartPageQty(scope = document) {
  if (!qs('.ks-cart-page', scope)) return;

  document.addEventListener('click', async e => {
    const btn = e.target.closest('[data-cart-page-qty]');
    if (!btn) return;
    const line = parseInt(btn.dataset.line);
    const qty  = parseInt(btn.dataset.qty);
    const res  = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line, quantity: qty })
    });
    if (res.ok) window.location.reload();
  });
}

/* ── Slideshow ───────────────────────────────────────────── */
function initSlideshow(scope = document) {
  qsa('.ks-slideshow', scope).forEach(slideshow => {
    const slides = qsa('.ks-slideshow__slide', slideshow);
    if (slides.length <= 1) return;

    const prevBtn  = qs('[data-slide-prev]', slideshow);
    const nextBtn  = qs('[data-slide-next]', slideshow);
    const dotsWrap = qs('.ks-slideshow__dots', slideshow);
    const autoplay = slideshow.dataset.autoplay === 'true';
    const speed    = parseInt(slideshow.dataset.speed || '5') * 1000;
    const isFade   = slideshow.classList.contains('ks-slideshow--transition-fade');
    let current    = 0;
    let timer;

    function goTo(index) {
      slides[current].classList.remove('is-active');
      dotsWrap?.children[current]?.classList.remove('is-active');
      current = (index + slides.length) % slides.length;
      slides[current].classList.add('is-active');
      dotsWrap?.children[current]?.classList.add('is-active');

      if (!isFade) {
        const track = qs('.ks-slideshow__track', slideshow);
        if (track) track.style.transform = `translateX(-${current * 100}%)`;
      }
    }

    if (dotsWrap) {
      slides.forEach((_, i) => {
        const dot = document.createElement('button');
        dot.className = 'ks-slideshow__dot' + (i === 0 ? ' is-active' : '');
        dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        dot.addEventListener('click', () => { goTo(i); resetTimer(); });
        dotsWrap.appendChild(dot);
      });
    }

    slides[0].classList.add('is-active');
    prevBtn?.addEventListener('click', () => { goTo(current - 1); resetTimer(); });
    nextBtn?.addEventListener('click', () => { goTo(current + 1); resetTimer(); });

    function resetTimer() {
      clearInterval(timer);
      if (autoplay) timer = setInterval(() => goTo(current + 1), speed);
    }
    resetTimer();
  });
}

/* ── Custom Cart Drawer ──────────────────────────────────── */
const KSCart = (() => {
  const drawer   = qs('#ks-cart-drawer');
  const overlay  = qs('#ks-cart-overlay');
  const itemsEl  = qs('[data-cart-items]');
  const footerEl = qs('[data-cart-footer]');
  const totalEl  = qs('[data-cart-total]');
  const emptyEl  = qs('.ks-cart-empty');
  const badges   = qsa('[data-cart-count]');

  function updateBadges(count) {
    badges.forEach(b => {
      b.textContent = count;
      b.classList.toggle('is-visible', count > 0);
    });
  }

  function renderItems(cart) {
    if (!itemsEl) return;
    itemsEl.innerHTML = '';

    if (cart.item_count === 0) {
      if (emptyEl) emptyEl.hidden = false;
      if (footerEl) footerEl.hidden = true;
      return;
    }

    if (emptyEl) emptyEl.hidden = true;
    if (footerEl) footerEl.hidden = false;
    if (totalEl) totalEl.textContent = formatMoney(cart.total_price);

    cart.items.forEach((item, i) => {
      const el = document.createElement('div');
      el.className = 'ks-cart-item';
      el.dataset.key = item.key;

      const imgSrc = item.featured_image
  ? item.featured_image.url.replace(/(\.\w+)$/, '_320x427$1')
  : '';

      el.innerHTML = `
        <a href="${item.url}" class="ks-cart-item__img">
          ${imgSrc ? `<img src="${imgSrc}" alt="${item.title}" width="80" height="107" loading="lazy">` : ''}
        </a>
        <div class="ks-cart-item__info">
          <p class="ks-cart-item__title">${item.product_title}</p>
          ${item.variant_title && item.variant_title !== 'Default Title'
            ? `<p class="ks-cart-item__variant">${item.variant_title}</p>` : ''}
          <p class="ks-cart-item__price">${formatMoney(item.final_line_price)}</p>
          <div class="ks-cart-item__qty">
            <button data-qty-change data-line="${i + 1}" data-qty="${item.quantity - 1}" aria-label="Decrease quantity">−</button>
            <span>${item.quantity}</span>
            <button data-qty-change data-line="${i + 1}" data-qty="${item.quantity + 1}" aria-label="Increase quantity">+</button>
          </div>
        </div>
        <button class="ks-cart-item__remove" data-qty-change data-line="${i + 1}" data-qty="0" aria-label="Remove ${item.product_title}">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round">
            <line x1="13" y1="1" x2="1" y2="13"/><line x1="1" y1="1" x2="13" y2="13"/>
          </svg>
        </button>
      `;
      itemsEl.appendChild(el);
    });
  }

  async function fetchCart() {
    const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
    return res.json();
  }

  async function open() {
    const cart = await fetchCart();
    renderItems(cart);
    updateBadges(cart.item_count);
    if (drawer) drawer.setAttribute('aria-hidden', 'false');
    if (overlay) overlay.classList.add('is-visible');
    document.body.style.overflow = 'hidden';
    qs('.ks-cart-drawer__close')?.focus();
  }

  function close() {
    if (drawer) drawer.setAttribute('aria-hidden', 'true');
    if (overlay) overlay.classList.remove('is-visible');
    document.body.style.overflow = '';
  }

  async function addItem(formData) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Accept': 'application/json' },
      body: formData
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.description || 'Could not add to cart');
    }
    return res.json();
  }

  async function changeQty(line, quantity) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ line, quantity })
    });
    const cart = await res.json();
    renderItems(cart);
    updateBadges(cart.item_count);
  }

  document.addEventListener('click', async e => {
    if (e.target.closest('[data-cart-open]')) { open(); return; }
    if (e.target.closest('[data-cart-close]')) { close(); return; }
    const qtyBtn = e.target.closest('[data-qty-change]');
    if (qtyBtn) {
      const line = parseInt(qtyBtn.dataset.line);
      const qty  = parseInt(qtyBtn.dataset.qty);
      await changeQty(line, qty);
      return;
    }
    if (e.target.closest('[data-checkout-btn]')) {
      window.location.href = '/checkout';
    }
  });

  document.addEventListener('submit', async e => {
    const form = e.target.closest('[data-atc-form]');
    if (!form) return;
    e.preventDefault();

    const btn = form.querySelector('[data-atc-btn]');
    const originalLabel = btn?.textContent.trim() || 'Add to Cart';
    if (btn) { btn.disabled = true; btn.textContent = 'Adding...'; }

    try {
      const cartType = document.documentElement.dataset.cartType
        || document.body.dataset.cartType
        || 'drawer';
      await addItem(new FormData(form));
      if (cartType === 'page') {
        window.location.href = '/cart';
      } else {
        await open();
      }
    } catch (err) {
      alert(err.message);
    } finally {
      if (btn) { btn.disabled = false; btn.textContent = originalLabel; }
    }
  });

  document.addEventListener('keydown', e => {
    if (drawer && e.key === 'Escape' && drawer.getAttribute('aria-hidden') === 'false') close();
  });

  fetchCart().then(cart => updateBadges(cart.item_count)).catch(() => {});

  return { open, close, fetchCart };
})();

/* ── initAll: called on load + shopify:section:load ─────── */
function initAll(scope = document) {
  initReveal(scope);
  initStickyHeader(scope);
  initMobileNav(scope);
  initAccordion(scope);
  initSizePills(scope);
  initGallery(scope);
  initMobileGalleryDots(scope);
  initSizeGuide(scope);
  initEmailCapture(scope);
  initScrollTilt(scope);
  initCartPageQty(scope);
  initSlideshow(scope);
}

document.addEventListener('DOMContentLoaded', () => initAll());
document.addEventListener('shopify:section:load', (e) => initAll(e.target));
